import { CheckoutProgress } from "@/components/checkout/CheckoutProgress";
import { ErrorBoundary } from "@/components/error-boundary";
import { OrderSummary } from "@/components/order-summary";
import { useFloatingButtonsClearance } from "@/components/support/useFloatingButtonsLift";
import { Skeleton } from "@/components/ui/skeleton";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useCartPromoCodes } from "@/hooks/useCartPromoCodes";
import { useElementBounds } from "@/hooks/useElementBounds";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { useReportOnce } from "@/hooks/useReportOnce";
import { analytics } from "@/lib/analytics";
import { isMixedPaymentModes } from "@/lib/supplier-collected";
import { useSellerCarts } from "@/queries/cart";
import { calculateSummary } from "@/utils/cart";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { cn } from "@repo/design-system/utils";
import { createRoute, Link, useSearch } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { memo, useMemo, useState, type CSSProperties } from "react";
import { z } from "zod";
import { layoutRoute } from "../$layoutRoute";
import { BackToCartLink } from "./components/BackToCartLink";
import { CheckoutActionSlotContext } from "./components/CheckoutActionSlot";
import { CheckoutSummarySheetContext } from "./components/CheckoutSummarySheet";
import { PaymentStep, ShippingStep } from "./sections";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;
type StoreSellerCartsSummary = SalonStockerHttpTypes.StoreSellerCartsSummary;

/**
 * Search params schema for checkout route
 * - cart_ids: Comma-separated list of cart IDs to checkout
 * - step: Current checkout step (address, shipping, review)
 */
const checkoutSearchSchema = z.object({
  cart_ids: z.string().optional(),
  step: z.enum(["information", "payment"]).optional().default("information")
});

export type CheckoutSearchParams = z.infer<typeof checkoutSearchSchema>;

export const checkoutRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: "checkout",
  component: CheckoutPage,
  validateSearch: checkoutSearchSchema
});

/**
 * Main checkout page with two-column layout.
 *
 * Layout:
 * - Left: Step-based forms (Address, Shipping Method, Review)
 * - Right: Persistent Order Summary sidebar
 *
 * Supports multi-cart checkout where multiple seller carts
 * can be checked out simultaneously.
 */
function CheckoutPage() {
  const { cart_ids, step } = useSearch({ from: checkoutRoute.id });

  // Parse cart IDs from search params
  const selectedCartIds = useMemo(() => {
    if (!cart_ids) return [];
    return cart_ids.split(",").filter(Boolean);
  }, [cart_ids]);

  // Fetch all carts
  const { data: allCartsData, isLoading, error } = useSellerCarts();

  // Filter to only selected carts
  const selectedCarts = useMemo(() => {
    if (!allCartsData?.carts) return [];
    if (selectedCartIds.length === 0) {
      // If no specific carts selected, use all carts
      return allCartsData.carts;
    }
    return allCartsData.carts.filter((cart) =>
      selectedCartIds.includes(cart.cart_id)
    );
  }, [allCartsData?.carts, selectedCartIds]);

  // Calculate summary for selected carts
  const summary = useMemo(
    () => calculateSummary(selectedCarts),
    [selectedCarts]
  );

  // Funnel step 5: the salon reached checkout with a basket.
  //
  // The pilot runs with `enable_external_checkout: false`, so
  // `external_checkout_confirm_clicked` never fires - without this event there
  // is no signal at all that anybody got this far, and a drop between the cart
  // and the order would be indistinguishable from never having tried.
  //
  // A constant key, so this reports once per checkout entry: moving between the
  // information and payment steps re-renders with new search params but the key
  // never changes and never goes null, so there is nothing to re-arm it. Having
  // carts is readiness rather than part of the key - the basket emptying under
  // us (the order completing and invalidating the cart list) must not let a
  // refill read as a second checkout entry.
  useReportOnce(
    "checkout",
    () => {
      analytics.checkoutStarted({
        cart_count: selectedCarts.length,
        item_count: summary.total_items,
        total: summary.grand_total,
        // The seller-cart payload carries the currency now (KAN-47), so this
        // no longer has to report null. Every cart shares the buyer's region,
        // so the first one answers for the basket. Still nullable on the
        // schema: readiness here is "has carts", and the guard is cheap.
        currency_code: selectedCarts[0]?.currency_code ?? null
      });
    },
    selectedCarts.length > 0
  );

  // Promo code management
  const { appliedPromoCodes, promoErrors, applyPromo, removePromo } =
    useCartPromoCodes();

  // Where the step's primary action portals to on desktop: under the summary's
  // total, as on the cart. State rather than a ref so the step re-renders once
  // the slot exists.
  const [actionSlot, setActionSlot] = useState<HTMLElement | null>(null);

  // The summary sticks below the sticky header, whose height is not fixed (the
  // "Checkout" title drops out on short viewports), so it is measured rather
  // than guessed and handed to the aside as CSS variables: its height for the
  // sticky offset inside the page's scroll area, and its on-screen bottom edge
  // (which also counts the app header above that scroll area) for the space
  // left below it.
  const [headerRef, headerBounds] = useElementBounds<HTMLDivElement>();
  const isDesktop = useBreakpoint("lg");
  const floatingButtonsClearance = useFloatingButtonsClearance();

  // What the phone bar's total expands into: the summary card's own roll-up,
  // read-only (promo entry stays in the card at the end of the page). Built
  // here, where the carts and totals live, and mounted by the bar only while
  // open, so it is never a second "Order summary" region on the page at rest.
  const summarySheet = useMemo(
    () => (
      <OrderSummary mode="checkout" carts={selectedCarts} summary={summary} />
    ),
    [selectedCarts, summary]
  );

  // Loading state
  if (isLoading) {
    return <CheckoutPageSkeleton />;
  }

  // Error state
  if (error) {
    return <CheckoutErrorState error={error} />;
  }

  // Empty cart state
  if (!allCartsData || selectedCarts.length === 0) {
    return <EmptyCheckoutState />;
  }

  // Defense-in-depth against a hand-crafted URL. The cart page blocks a
  // mode-mixed "Checkout all", but a direct `/checkout?cart_ids=...` link could
  // still put a supplier-collected cart and a marketplace cart in one checkout -
  // which no single payment step can settle (one takes a card, the other must
  // not). Refuse it here too rather than let PaymentStep guess a mode for the
  // whole set (KAN-131).
  if (isMixedPaymentModes(selectedCarts)) {
    return <MixedCheckoutState />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header Section */}
      <div ref={headerRef} className="sticky top-0 z-10 bg-gray-50 pb-3 pt-3">
        <div className="container mx-auto max-w-[1400px] px-4">
          {/* Checkout progress */}
          <CheckoutProgress
            currentStep="checkout"
            checkoutSubStep={{ current: step, cartIds: selectedCartIds }}
          />

          {/* Header - hidden below 768px tall (as on the cart page); the
              breadcrumb already names the step and a short viewport can't spare
              the band. */}
          <div className="mt-4 flex items-center justify-between [@media(max-height:767px)]:hidden">
            <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>
          </div>
        </div>
      </div>

      {/* Extra bottom padding below `lg`, where the step action is a fixed
          bottom bar: the order summary stacks last on a phone and would sit
          behind that bar (its "What to Expect" unreadable) without room to
          scroll clear of it. */}
      <div className="container mx-auto max-w-[1400px] px-4 pb-24 lg:pb-8">
        {/* Two columns on desktop, one stacked column below `lg` - the 380px
            summary aside overflowed a 360px viewport. See storefront#145 (P4). */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* LEFT COLUMN - Form Steps */}
          <main className="min-w-0 flex-1">
            <ErrorBoundary context="checkout form">
              <CheckoutActionSlotContext.Provider value={actionSlot}>
                <CheckoutSummarySheetContext.Provider value={summarySheet}>
                  <CheckoutStepContent
                    step={step}
                    carts={selectedCarts}
                    summary={summary}
                    cartIds={selectedCarts.map((c) => c.cart_id)}
                  />
                </CheckoutSummarySheetContext.Provider>
              </CheckoutActionSlotContext.Provider>
            </ErrorBoundary>
          </main>

          {/* RIGHT COLUMN - Order Summary */}
          <aside className="w-full flex-shrink-0 lg:w-[380px]">
            {/* Sticky so the step action under the total stays in view while
                the buyer works down a long form. Capped to the space below the
                header and scrollable, with the action pinned to its bottom (see
                OrderSummary), so a tall summary (many suppliers) can never push
                the button off-screen. The cap also stops short of the floating
                voice-order, WhatsApp and (when the salon has it) order
                assistant buttons stacked in the bottom-right corner
                (useFloatingButtonsClearance, which grows by the assistant
                button only while it is mounted), which would otherwise cover
                the pinned action on narrower desktops. */}
            <div
              className="lg:sticky lg:top-[var(--checkout-sticky-top)] lg:max-h-[calc(100vh-var(--checkout-header-bottom)-var(--checkout-floating-clearance))] lg:overflow-y-auto"
              style={
                {
                  "--checkout-sticky-top": `${headerBounds.height}px`,
                  "--checkout-header-bottom": `${headerBounds.bottom}px`,
                  "--checkout-floating-clearance": floatingButtonsClearance
                } as CSSProperties
              }
            >
              <ErrorBoundary context="order summary">
                <OrderSummary
                  mode="checkout"
                  carts={selectedCarts}
                  summary={summary}
                  onApplyPromo={applyPromo}
                  onRemovePromo={removePromo}
                  appliedPromoCodes={appliedPromoCodes}
                  promoErrors={promoErrors}
                  actionSlotRef={setActionSlot}
                />
              </ErrorBoundary>
            </div>
          </aside>
        </div>

        {/* The way back on a phone: after the order summary, centred, the end
            of the page where the buyer has reviewed everything. From `lg` it
            sits under the primary action in the order summary instead, and the
            breadcrumb's Cart step stays pinned above at every width. Padded to
            a 44px tap target. */}
        {!isDesktop && (
          <div className="mt-2 flex justify-center">
            <BackToCartLink className="min-h-11 px-3" />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders the appropriate step content based on current step
 */
interface CheckoutStepContentProps {
  step: "information" | "payment";
  carts: StoreSellerCart[];
  summary: StoreSellerCartsSummary;
  cartIds: string[];
}

const CheckoutStepContent = memo(function CheckoutStepContent({
  step,
  carts,
  summary,
  cartIds
}: CheckoutStepContentProps) {
  switch (step) {
    case "information":
      return <ShippingStep cartIds={cartIds} carts={carts} />;
    case "payment":
      return <PaymentStep cartIds={cartIds} carts={carts} />;
    // return <ReviewStepPlaceholder />;
    default:
      return <ShippingStep cartIds={cartIds} carts={carts} />;
  }
});

/**
 * Placeholder for Shipping Step - will be replaced with actual implementation
 */
const ShippingStepPlaceholder = memo(function ShippingStepPlaceholder() {
  return (
    <section
      className="rounded-xl border border-gray-200 bg-white"
      aria-labelledby="shipping-method-heading"
    >
      <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">
          3
        </span>
        <h2
          id="shipping-method-heading"
          className="text-lg font-semibold text-gray-900"
        >
          Shipping Method
        </h2>
      </div>
      <div className="p-6">
        <p className="text-gray-500">
          Shipping method selection will be implemented here...
        </p>
      </div>
    </section>
  );
});

/**
 * Placeholder for Review Step - will be replaced with actual implementation
 */
const ReviewStepPlaceholder = memo(function ReviewStepPlaceholder() {
  return (
    <section
      className="rounded-xl border border-gray-200 bg-white"
      aria-labelledby="order-review-heading"
    >
      <div className="flex items-center gap-3 border-b border-gray-200 px-6 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-600">
          4
        </span>
        <h2
          id="order-review-heading"
          className="text-lg font-semibold text-gray-900"
        >
          Review Order
        </h2>
      </div>
      <div className="p-6">
        <p className="text-gray-500">
          Order review will be implemented here...
        </p>
      </div>
    </section>
  );
});

/**
 * Loading skeleton for checkout page
 */
const CheckoutPageSkeleton = memo(function CheckoutPageSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-[1400px] px-4 py-6">
        {/* Breadcrumb skeleton */}
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-16" />
        </div>

        {/* Header skeleton */}
        <div className="mb-6 mt-4 flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-5 w-28" />
        </div>

        {/* Two-column layout skeleton - stacks below `lg`, like the page it
            stands in for. */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Left column */}
          <div className="flex-1 space-y-6">
            {/* Section 1 */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-6 w-40" />
              </div>
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
            {/* Section 2 */}
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="mb-4 flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-6 w-36" />
              </div>
              <Skeleton className="mb-2 h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>

          {/* Right column */}
          <div className="w-full lg:w-[380px]">
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <Skeleton className="mb-2 h-6 w-32" />
              <Skeleton className="mb-6 h-4 w-20" />
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
              <div className="mt-6 space-y-2 border-t border-gray-200 pt-4">
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
                <div className="flex justify-between pt-2">
                  <Skeleton className="h-5 w-12" />
                  <Skeleton className="h-6 w-24" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

/**
 * Error state for checkout page
 */
const CheckoutErrorState = memo(function CheckoutErrorState({
  error
}: {
  error: Error;
}) {
  // Reported, not shown: the server's message means nothing to a salon and can
  // leak internals.
  useReportLoadFailure(error, "checkout page");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-2xl px-4 py-16">
        <div
          className="rounded-xl border border-red-200 bg-white p-12 text-center shadow-sm"
          role="alert"
        >
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <span className="text-2xl text-red-600" aria-hidden="true">
              !
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            Something went wrong
          </h1>
          <p className="mb-8 text-gray-600">
            We couldn&apos;t load your checkout. Please try again.
          </p>
          <Link
            to="/cart"
            className={cn(
              "inline-flex items-center justify-center",
              "rounded-lg bg-purple-600 px-6 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            )}
          >
            Return to Cart
          </Link>
        </div>
      </div>
    </div>
  );
});

/**
 * Shown when a checkout URL names carts spanning both payment modes. Not
 * reachable from the cart page (which blocks the combined action) - only from a
 * hand-crafted link - so it explains the constraint and sends the salon back to
 * the cart to check the suppliers out separately.
 *
 * Copy is intentionally its own: a full-page dead-end warrants a fuller
 * explanation than the cart page's transient toast (`MIXED_PAYMENT_MODES_MESSAGE`),
 * so the two are not shared.
 */
const MixedCheckoutState = memo(function MixedCheckoutState() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-2xl px-4 py-16">
        <div
          className="rounded-xl border border-amber-200 bg-white p-12 text-center shadow-sm"
          role="alert"
        >
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
            <span className="text-2xl text-amber-600" aria-hidden="true">
              !
            </span>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            These suppliers can&apos;t be checked out together
          </h1>
          <p className="mb-8 text-gray-600">
            Some of your suppliers collect payment directly and can&apos;t be
            combined with card-paid suppliers in one checkout. Head back to your
            cart and check them out separately.
          </p>
          <Link
            to="/cart"
            className={cn(
              "inline-flex items-center justify-center gap-2",
              "rounded-lg bg-purple-600 px-6 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Go to Cart
          </Link>
        </div>
      </div>
    </div>
  );
});

/**
 * Empty checkout state when no carts are available
 */
const EmptyCheckoutState = memo(function EmptyCheckoutState() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto max-w-2xl px-4 py-16">
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-purple-100">
            <ArrowLeft className="h-8 w-8 text-purple-600" aria-hidden="true" />
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            No items to checkout
          </h1>
          <p className="mb-8 text-gray-600">
            Your cart is empty. Add some items before proceeding to checkout.
          </p>
          <Link
            to="/cart"
            className={cn(
              "inline-flex items-center justify-center gap-2",
              "rounded-lg bg-purple-600 px-6 py-3",
              "text-sm font-medium text-white",
              "transition-colors hover:bg-purple-700",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2"
            )}
          >
            <ArrowLeft className="h-4 w-4" />
            Go to Cart
          </Link>
        </div>
      </div>
    </div>
  );
});
